import React from 'react'
import classNames from 'classnames'

interface PillPrompt {
  id: string
  text: string
  context?: unknown
}

interface PillPromptsProps {
  prompts: PillPrompt[]
  onPillClick: (_prompt: PillPrompt) => void
  loading?: boolean
  className?: string
}

const PillPrompts: React.FC<PillPromptsProps> = ({
  prompts,
  onPillClick,
  loading = false,
  className = '',
}) => {
  const [clickedPillId, setClickedPillId] = React.useState<string | null>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  const checkScrollPosition = React.useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }, [])

  React.useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    checkScrollPosition()

    container.addEventListener('scroll', checkScrollPosition, { passive: true })

    const timeoutId = setTimeout(checkScrollPosition, 100)

    return () => {
      container.removeEventListener('scroll', checkScrollPosition)
      clearTimeout(timeoutId)
    }
  }, [prompts, checkScrollPosition])

  React.useEffect(() => {
    const handleResize = () => {
      setTimeout(checkScrollPosition, 100)
    }

    window.addEventListener('resize', handleResize, { passive: true })
    return () => window.removeEventListener('resize', handleResize)
  }, [checkScrollPosition])

  if (!prompts || prompts.length === 0) {
    return null
  }

  if (loading) {
    return (
      <div className={classNames('d-flex overflow-auto flex-nowrap', className)}>
        {[1, 2, 3, 4, 5].map(index => (
          <div
            key={`skeleton-${index}`}
            className='badge rounded-pill border bg-light text-muted placeholder-glow'
            style={{
              minWidth: '10rem',
              height: '2.25rem',
            }}
          >
            <span
              className='placeholder rounded-pill'
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
              }}
            ></span>
          </div>
        ))}
      </div>
    )
  }

  const containerClasses = classNames(
    'd-flex gap-2 mb-3 overflow-auto flex-nowrap pilot-pill-prompts',
    {
      'pilot-pill-prompts--loading': loading,
      'pilot-pill-prompts--can-scroll-left': canScrollLeft,
      'pilot-pill-prompts--can-scroll-right': canScrollRight,
    },
    className
  )

  const pillClasses = (isClicked: boolean = false) =>
    classNames(
      'badge rounded-pill fs-4 border pilot-pill-prompt',
      'bg-transparent text-primary border-primary',
      {
        'pilot-pill-prompt--loading': loading,
        'pilot-pill-prompt--clicked': isClicked,
        'pilot-pill-prompt--interactive': !loading,
      }
    )

  const handlePillClick = (promptItem: PillPrompt) => {
    if (loading) {
      return
    }

    setClickedPillId(promptItem.id)
    onPillClick(promptItem)

    setTimeout(() => {
      setClickedPillId(null)
    }, 200)
  }

  return (
    <div className='pilot-pill-prompts-wrapper'>
      <div
        ref={scrollContainerRef}
        className={containerClasses}
        style={{
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {prompts.map(promptItem => {
          const isClicked = clickedPillId === promptItem.id
          return (
            <button
              key={promptItem.id}
              type='button'
              className={pillClasses(isClicked)}
              style={{
                padding: '0.15rem 0.5rem',
                lineHeight: 1.5,
              }}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handlePillClick(promptItem)
              }}
              disabled={loading}
            >
              {promptItem.text}
            </button>
          )
        })}
      </div>

      {canScrollLeft && <div className='pilot-pill-prompts__fade pilot-pill-prompts__fade--left' />}
      {canScrollRight && (
        <div className='pilot-pill-prompts__fade pilot-pill-prompts__fade--right' />
      )}
    </div>
  )
}

export default PillPrompts
